import { Order } from './components/sortingFunctions';
import { PaginationOrSortParameters } from './types';
import { useState, useCallback, useEffect, useMemo } from 'react';

export enum OrderDirection {
  ASC = 'asc',
  DESC = 'desc',
}

interface SortOrder {
  orderBy: string;
  orderDirection: Order;
}

interface Props<T> {
  initialData: T[];
  initialRowsPerPage?: number;
}

export const usePaginationAndSort = <T>({
  initialData,
  initialRowsPerPage = 25,
}: Props<T>) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const sortedData = useMemo(() => {
    if (!sortOrder) {
      return initialData;
    }

    const { orderBy, orderDirection } = sortOrder;

    // account for null values
    return [...(initialData || [])].sort((a, b) => {
      switch (true) {
        case a[orderBy] === null && b[orderBy] === null:
          return 0;
        case a[orderBy] === null:
          return orderDirection === OrderDirection.ASC ? -1 : 1;
        case b[orderBy] === null:
          return orderDirection === OrderDirection.ASC ? 1 : -1;
      }

      if (orderDirection === OrderDirection.ASC) {
        return a[orderBy] > b[orderBy] ? 1 : -1;
      } else {
        return a[orderBy] < b[orderBy] ? 1 : -1;
      }
    });
  }, [initialData, sortOrder]);

  const paginatedAndSortedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [initialData]);

  const handleSortChange = useCallback(
    (newOrderBy: string, newOrderDirection: Order) => {
      setSortOrder({
        orderBy: newOrderBy,
        orderDirection: newOrderDirection,
      });
      setPage(0);
    },
    []
  );

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const onPaginationOrSort = useCallback(
    (parameters: PaginationOrSortParameters) => {
      if (parameters.property && parameters.sortOrder) {
        handleSortChange(parameters.property, parameters.sortOrder);
      } else if (parameters.page !== undefined) {
        setPage(parameters.page);
      } else if (parameters.rowsPerPage !== undefined) {
        handleRowsPerPageChange(parameters.rowsPerPage);
      }
    },
    [handleSortChange, handleRowsPerPageChange]
  );

  return {
    page,
    rowsPerPage,
    paginatedData: paginatedAndSortedData,
    handleSortChange,
    handleRowsPerPageChange,
    onPaginationOrSort,
    setPage,
    setRowsPerPage,
  };
};
